/**
 * Markdown blocks as OOXML. Tracked (the default), every run, row and
 * paragraph mark is an insertion authored "mytender.io": inserted with change
 * tracking off, Word keeps the marks, so the user sees exactly what mytender
 * brought in and accepts or rejects it like any other revision. Plain, the
 * same content carries no revision marks: a first pull into empty space is
 * content, not a change to review. Optionally wrapped in a block-level
 * content control: that is a question's answer block.
 */
import type { TemplateStyleMap } from "./identity";
import { REVISION_AUTHOR, ooxmlPackage } from "./authoredRevision";
import type { Block, Inline } from "./markdown";

/** Characters XML 1.0 forbids (Word's manual line break, U+000B, among them). */
// eslint-disable-next-line no-control-regex -- the point is to name them
const XML_FORBIDDEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;

const esc = (text: string): string =>
  text
    .replace(XML_FORBIDDEN, "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export type XmlOptions = {
  tracked?: boolean;
  author?: string;
  /** The template's styles to write in; `undefined` takes the applied template's, `null` Word's built-ins. */
  styles?: TemplateStyleMap | null;
};

type Stamp = { tracked: boolean; author: string; date: string; next: () => string; styles: TemplateStyleMap | null };

/**
 * The applied template's style map, set when a template is applied and when
 * the pane opens a document that has one, so every builder here writes in
 * the template's styles without each caller threading it through.
 */
let appliedStyles: TemplateStyleMap | null = null;
export const setTemplateStyleMap = (map: TemplateStyleMap | null | undefined): void => {
  appliedStyles = map ?? null;
};
export const templateStyleMap = (): TemplateStyleMap | null => appliedStyles;

const stamp = ({ tracked = true, author = REVISION_AUTHOR, styles }: XmlOptions = {}): Stamp => {
  let id = Math.floor(Math.random() * 1_000_000) + 1000;
  return {
    tracked,
    author: esc(author),
    date: new Date().toISOString().replace(/\.\d{3}Z$/, "Z"),
    next: () => String(id++),
    styles: styles === undefined ? appliedStyles : styles,
  };
};

const pStyle = (id: string | undefined | null): string =>
  id && id !== "Normal" ? `<w:pStyle w:val="${esc(id)}"/>` : "";
const headingStyle = (s: Stamp, level: number): string => {
  const n = Math.min(6, Math.max(1, level));
  return `<w:pStyle w:val="${esc(s.styles?.headings?.[String(n)]?.id ?? `Heading${n}`)}"/>`;
};

const insAttrs = (s: Stamp) => `w:id="${s.next()}" w:author="${s.author}" w:date="${s.date}"`;
/** `inner` as a tracked insertion, or as is when plain. */
const ins = (s: Stamp, inner: string) => (s.tracked ? `<w:ins ${insAttrs(s)}>${inner}</w:ins>` : inner);
/** The paragraph-mark (or row) insertion mark, or nothing when plain. */
const markIns = (s: Stamp) => (s.tracked ? `<w:ins ${insAttrs(s)}/>` : "");

/**
 * Inlines as runs inside tracked insertions. A link is a HYPERLINK field
 * (w:fldSimple), which needs no relationship part in the inserted package.
 */
const runs = (inlines: Inline[], s: Stamp, prefix = ""): string => {
  const all: Inline[] = prefix ? [{ text: prefix }, ...inlines] : inlines;
  const run = (i: Inline) => {
    const props =
      (i.href ? '<w:rStyle w:val="Hyperlink"/><w:color w:val="0563C1"/><w:u w:val="single"/>' : "") +
      (i.bold ? "<w:b/>" : "") +
      (i.italic ? "<w:i/>" : "");
    return `<w:r>${props ? `<w:rPr>${props}</w:rPr>` : ""}<w:t xml:space="preserve">${esc(i.text)}</w:t></w:r>`;
  };
  return all
    .map((i) => {
      const body = ins(s, run(i));
      return i.href
        ? `<w:fldSimple w:instr="${esc(` HYPERLINK "${i.href.replace(/"/g, "%22")}" `)}">${body}</w:fldSimple>`
        : body;
    })
    .join("");
};

const paragraph = (inlines: Inline[], s: Stamp, pPrInner = "", prefix = ""): string => {
  const pPr = pPrInner + (s.tracked ? `<w:rPr>${markIns(s)}</w:rPr>` : "");
  return `<w:p>${pPr ? `<w:pPr>${pPr}</w:pPr>` : ""}${runs(inlines, s, prefix)}</w:p>`;
};

const INDENT_TWIPS = 360;
const listIndent = (depth: number) => `<w:ind w:left="${INDENT_TWIPS * (depth + 1)}" w:hanging="${INDENT_TWIPS}"/>`;

const tableXml = (rows: Inline[][][], header: boolean, s: Stamp): string => {
  const width = Math.max(1, ...rows.map((r) => r.length));
  const border = (side: string) => `<w:${side} w:val="single" w:sz="4" w:space="0" w:color="BFBFBF"/>`;
  // The template's table style when it has one (its borders and shading);
  // plain grey borders otherwise.
  const tableStyle = s.styles?.table?.id;
  const tblPr = tableStyle
    ? `<w:tblPr><w:tblStyle w:val="${esc(tableStyle)}"/><w:tblW w:w="5000" w:type="pct"/><w:tblLook w:val="04A0" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/></w:tblPr>`
    : `<w:tblPr><w:tblW w:w="5000" w:type="pct"/><w:tblBorders>${["top", "left", "bottom", "right", "insideH", "insideV"].map(border).join("")}</w:tblBorders>` +
      `<w:tblCellMar><w:left w:w="80" w:type="dxa"/><w:right w:w="80" w:type="dxa"/></w:tblCellMar></w:tblPr>`;
  const grid = `<w:tblGrid>${Array.from({ length: width }, () => `<w:gridCol w:w="${Math.floor(9000 / width)}"/>`).join("")}</w:tblGrid>`;
  const body = rows
    .map((cells, r) => {
      const bold = header && r === 0;
      const tcs = Array.from({ length: width }, (_, c) => {
        const inlines = (cells[c] ?? []).map((i) => (bold ? { ...i, bold: true } : i));
        return `<w:tc><w:tcPr><w:tcW w:w="0" w:type="auto"/></w:tcPr>${paragraph(inlines, s)}</w:tc>`;
      }).join("");
      return `<w:tr><w:trPr>${markIns(s)}${bold ? "<w:tblHeader/>" : ""}</w:trPr>${tcs}</w:tr>`;
    })
    .join("");
  return `<w:tbl>${tblPr}${grid}${body}</w:tbl>`;
};

/**
 * A table preceded by an empty paragraph, as a tracked insertion: what
 * `replace_table` puts after the deleted table (Word needs a paragraph
 * between two tables, or it merges them).
 */
export const tableAfterParagraphXml = (rows: Inline[][][], header: boolean, options: XmlOptions = {}): string => {
  const s = stamp(options);
  return paragraph([], s) + tableXml(rows, header, s);
};

/** The body XML (paragraphs and tables) for some blocks, tracked unless told otherwise. */
export const blocksXml = (blocks: Block[], options: XmlOptions = {}): string => {
  const s = stamp(options);
  return blocks
    .map((b) => {
      switch (b.kind) {
        case "heading":
          return paragraph(b.inlines, s, headingStyle(s, b.level));
        case "paragraph":
          return paragraph(b.inlines, s, pStyle(s.styles?.body?.id));
        case "bullet":
        case "number": {
          // The template's list style carries its own bullet or number when
          // it has numbering; a typed marker with a hanging indent otherwise.
          const style = b.kind === "bullet" ? s.styles?.bullet : s.styles?.number;
          const marker = b.kind === "bullet" ? "•\t" : `${b.ordinal}.\t`;
          if (style?.has_numbering) {
            const level = b.depth > 0 ? `<w:numPr><w:ilvl w:val="${Math.min(8, b.depth)}"/></w:numPr>` : "";
            return paragraph(b.inlines, s, `<w:pStyle w:val="${esc(style.id)}"/>${level}`);
          }
          return paragraph(
            b.inlines,
            s,
            `<w:pStyle w:val="${esc(style?.id ?? "ListParagraph")}"/>${listIndent(b.depth)}`,
            marker
          );
        }
        case "table":
          // A paragraph must follow a table inside a content control.
          return tableXml(b.rows, b.header, s) + paragraph([], s);
        case "pagebreak":
          return `<w:p>${s.tracked ? `<w:pPr><w:rPr>${markIns(s)}</w:rPr></w:pPr>` : ""}${ins(s, '<w:r><w:br w:type="page"/></w:r>')}</w:p>`;
      }
    })
    .join("");
};

/** One heading paragraph (a question's heading above its block). */
export const headingXml = (text: string, level: number, options: XmlOptions = {}): string => {
  const s = stamp(options);
  return paragraph([{ text }], s, headingStyle(s, level));
};

/** A block-level content control around some body XML. */
export const blockControlXml = (inner: string, tag: string, title: string): string =>
  `<w:sdt><w:sdtPr><w:alias w:val="${esc(title)}"/><w:tag w:val="${esc(tag)}"/>` +
  `<w15:appearance xmlns:w15="http://schemas.microsoft.com/office/word/2012/wordml" w15:val="hidden"/>` +
  `</w:sdtPr><w:sdtContent>${inner || "<w:p/>"}</w:sdtContent></w:sdt>`;

export const packageOf = (bodyXml: string): string => ooxmlPackage(bodyXml);
